import { enableButtonClickTracking } from '@snowplow/browser-plugin-button-click-tracking';
import { enableLinkClickTracking } from '@snowplow/browser-plugin-link-click-tracking';
import {
  type Context,
  type ExtraContext,
} from '@utilitywarehouse/tracking-core';
import { TrackingProvider } from '@utilitywarehouse/tracking-react';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';

import { JOURNEY_MODE, useMode } from '@store/journey';

import { getSignupApplicationSnowplowContext } from '../getSignupApplicationSnowplowContext';
import { setGlobalSignupApplicationContext } from '../globalSignupApplicationContext';
import { tracker } from '../tracker';
import {
  SignupApplicationEntity,
  type SignupCbcCalcStateEntity,
  SignupCoreServicesEntity,
  SignupMobileServicesEntity,
  SignupRemoteAppointmentEntity,
  signupApplicationEntity,
  signupCbcCalcStateEntity,
  signupCoreServicesEntity,
  signupMobileServicesEntity,
  signupRemoteAppointmentEntity,
} from '../trackingEvents';
import { TrackingContext } from './Context';

interface TrackingContextProviderProps {
  children?: ReactNode | undefined;
}

const TrackingContextProvider: FC<TrackingContextProviderProps> = ({
  children,
}) => {
  const mode = useMode();
  const [applicationTrackingContext, setApplicationTrackingContext] =
    useState<SignupApplicationEntity>();
  const [coreServicesTrackingContext, setCoreServicesTrackingContext] =
    useState<SignupCoreServicesEntity>();
  const [signupMobileServicesContext, setSignupMobileServicesContext] =
    useState<SignupMobileServicesEntity>();
  const [signupRemoteAppointmentContext, setSignupRemoteAppointmentContext] =
    useState<SignupRemoteAppointmentEntity>();
  const [signupCbcCalcStateContext, setSignupCbcCalcStateContext] =
    useState<Nullable<SignupCbcCalcStateEntity>>(null);

  const trackingContexts = useMemo<ExtraContext<Context>[]>(() => {
    const contexts: ExtraContext<Context>[] = [];

    if (applicationTrackingContext) {
      contexts.push(signupApplicationEntity(applicationTrackingContext));
    }

    if (coreServicesTrackingContext) {
      contexts.push(signupCoreServicesEntity(coreServicesTrackingContext));
    }

    if (signupMobileServicesContext) {
      contexts.push(signupMobileServicesEntity(signupMobileServicesContext));
    }

    if (signupRemoteAppointmentContext) {
      contexts.push(
        signupRemoteAppointmentEntity(signupRemoteAppointmentContext)
      );
    }

    if (signupCbcCalcStateContext) {
      contexts.push(signupCbcCalcStateEntity(signupCbcCalcStateContext));
    }

    return contexts;
  }, [
    applicationTrackingContext,
    coreServicesTrackingContext,
    signupMobileServicesContext,
    signupRemoteAppointmentContext,
    signupCbcCalcStateContext,
  ]);

  const resetTrackingContext = (application?: SignupApplicationEntity) => {
    setApplicationTrackingContext(application);
    setCoreServicesTrackingContext(undefined);
    setSignupMobileServicesContext(undefined);
  };

  const handleCreateBasket = (application: SignupApplicationEntity) => {
    resetTrackingContext(application);
  };

  useEffect(() => {
    setGlobalSignupApplicationContext(applicationTrackingContext);
  }, [applicationTrackingContext]);

  useEffect(() => {
    if (mode === JOURNEY_MODE.SIGNUP) {
      enableLinkClickTracking({
        pseudoClicks: true,
        context: [getSignupApplicationSnowplowContext],
      });

      enableButtonClickTracking({
        context: [getSignupApplicationSnowplowContext],
      });
    }
  }, [mode]);

  return (
    <TrackingContext
      value={{
        applicationTrackingContext,
        setApplicationTrackingContext,
        coreServicesTrackingContext,
        setCoreServicesTrackingContext,
        setSignupMobileServicesContext,
        signupMobileServicesContext,
        signupRemoteAppointmentContext,
        setSignupRemoteAppointmentContext,
        signupCbcCalcStateContext,
        setSignupCbcCalcStateContext,
        trackingContexts,
        handleCreateBasket,
        resetTrackingContext,
      }}
    >
      <TrackingProvider client={tracker}>{children}</TrackingProvider>
    </TrackingContext>
  );
};

export { TrackingContextProvider };
